import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';

const emptyForm = {
  title: '',
};

function formatSize(size) {
  const n = Number(size || 0);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function DocumentsTab() {
  const [documents, setDocuments] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [file, setFile] = useState(null);
  const [replaceFile, setReplaceFile] = useState(null);
  const [replaceTarget, setReplaceTarget] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [message, setMessage] = useState('');
  const [lastCreated, setLastCreated] = useState(null);
  const [search, setSearch] = useState('');

  const filteredDocuments = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return documents;
    return documents.filter((doc) =>
      String(doc.serialNumber || '').toLowerCase().includes(term) ||
      String(doc.title || '').toLowerCase().includes(term) ||
      String(doc.originalName || '').toLowerCase().includes(term)
    );
  }, [documents, search]);

  const loadDocuments = async () => {
    setLoadingList(true);
    try {
      const res = await api.get('/documents');
      setDocuments(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setMessage(err.response?.data?.error || 'تعذر تحميل قائمة الوثائق');
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, []);

  const validatePdf = (selectedFile) => {
    if (!selectedFile) return false;
    if (selectedFile.type !== 'application/pdf') {
      setMessage('يسمح برفع ملفات PDF فقط');
      return false;
    }
    if (selectedFile.size > 50 * 1024 * 1024) {
      setMessage('حجم الملف أكبر من 50MB');
      return false;
    }
    return true;
  };

  const makeVerifyLink = (serialNumber) => {
    return `${window.location.origin}/?documentId=${encodeURIComponent(serialNumber)}`;
  };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setMessage(`تم نسخ ${label}`);
    } catch {
      setMessage(`تعذر نسخ ${label}`);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!validatePdf(file)) return;

    setLoading(true);
    setMessage('');
    setLastCreated(null);
    try {
      const fd = new FormData();
      fd.append('document', file);
      fd.append('title', form.title);
      const res = await api.post('/documents', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        timeout: 0,
      });
      setLastCreated(res.data);
      setForm(emptyForm);
      setFile(null);
      setMessage('تم رفع الوثيقة بنجاح');
      await loadDocuments();
    } catch (err) {
      setMessage(err.response?.data?.error || 'فشل رفع الوثيقة');
    } finally {
      setLoading(false);
    }
  };

  const handleReplace = async (e) => {
    e.preventDefault();
    if (!replaceTarget || !validatePdf(replaceFile)) return;

    setLoading(true);
    setMessage('');
    try {
      const fd = new FormData();
      fd.append('document', replaceFile);
      fd.append('title', replaceTarget.title || '');
      const res = await api.put(`/documents/${replaceTarget._id}/file`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        timeout: 0,
      });
      setLastCreated(res.data);
      setReplaceFile(null);
      setReplaceTarget(null);
      setMessage('تم استبدال الملف بنجاح مع بقاء نفس الرقم التسلسلي');
      await loadDocuments();
    } catch (err) {
      setMessage(err.response?.data?.error || 'فشل استبدال الوثيقة');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (doc) => {
    const ok = window.confirm(`هل تريد حذف الوثيقة ${doc.serialNumber}؟`);
    if (!ok) return;

    setLoading(true);
    setMessage('');
    try {
      await api.delete(`/documents/${doc._id}`);
      if (lastCreated?._id === doc._id) setLastCreated(null);
      setMessage('تم حذف الوثيقة بنجاح');
      await loadDocuments();
    } catch (err) {
      setMessage(err.response?.data?.error || 'فشل حذف الوثيقة');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div dir="rtl" className="space-y-6">
      <div className="rounded-xl border border-blue-100 bg-gradient-to-br from-blue-50 to-white p-5">
        <h2 className="text-2xl font-extrabold text-blue-900">إدارة وثائق PDF</h2>
        <p className="mt-2 text-sm leading-7 text-gray-600">
          ارفع ملف PDF وسيتم توليد رقم تسلسلي ورابط تحقق مباشر يمكن نسخه للمستخدم.
        </p>
      </div>

      {message && (
        <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-semibold text-blue-900">
          {message}
        </div>
      )}

      <form onSubmit={handleUpload} className="grid gap-4 rounded-xl border bg-white p-5 shadow-sm md:grid-cols-[1fr_1fr_auto] md:items-end">
        <div>
          <label className="mb-2 block text-sm font-bold text-gray-700">عنوان اختياري</label>
          <input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="مثال: شهادة مشاركة"
            className="w-full rounded-lg border p-3 outline-none focus:border-blue-500"
          />
        </div>
        <div>
          <label className="mb-2 block text-sm font-bold text-gray-700">ملف PDF</label>
          <input
            type="file"
            accept="application/pdf,.pdf"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="w-full rounded-lg border p-3"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="min-h-[48px] rounded-lg bg-blue-700 px-6 font-bold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? 'جاري الرفع...' : 'رفع الوثيقة'}
        </button>
      </form>

      {loading && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="mb-2 flex items-center justify-between text-sm font-bold text-amber-900">
            <span>الرجاء الانتظار، يتم معالجة ملف PDF</span>
            <span>تحميل</span>
          </div>
          <div className="h-3 overflow-hidden rounded-full bg-amber-100">
            <div className="h-full w-1/2 animate-pulse rounded-full bg-amber-500" />
          </div>
        </div>
      )}

      {lastCreated && (
        <div className="rounded-xl border border-green-200 bg-green-50 p-5 shadow-sm">
          <h3 className="text-lg font-extrabold text-green-900">بيانات الوصول السريع</h3>
          <div className="mt-4 grid gap-3 md:grid-cols-[1fr_auto_auto] md:items-center">
            <div className="rounded-lg bg-white p-3">
              <p className="text-xs font-bold text-gray-500">ID الوثيقة</p>
              <p className="font-mono text-xl font-black text-blue-800" dir="ltr">{lastCreated.serialNumber}</p>
              <p className="mt-1 break-all text-sm text-gray-600" dir="ltr">{makeVerifyLink(lastCreated.serialNumber)}</p>
            </div>
            <button
              onClick={() => copyText(lastCreated.serialNumber, 'ID')}
              className="rounded-lg bg-blue-700 px-4 py-3 text-sm font-bold text-white hover:bg-blue-800"
            >
              نسخ ID
            </button>
            <button
              onClick={() => copyText(makeVerifyLink(lastCreated.serialNumber), 'الرابط')}
              className="rounded-lg bg-slate-900 px-4 py-3 text-sm font-bold text-white hover:bg-slate-700"
            >
              نسخ الرابط
            </button>
          </div>
        </div>
      )}

      {replaceTarget && (
        <form onSubmit={handleReplace} className="rounded-xl border border-amber-200 bg-amber-50 p-5">
          <div className="flex flex-col justify-between gap-3 md:flex-row md:items-center">
            <div>
              <h3 className="text-lg font-extrabold text-amber-950">استبدال ملف الوثيقة</h3>
              <p className="font-mono text-sm text-amber-900" dir="ltr">{replaceTarget.serialNumber}</p>
            </div>
            <button
              type="button"
              onClick={() => {
                setReplaceTarget(null);
                setReplaceFile(null);
              }}
              className="rounded-lg border border-amber-300 px-4 py-2 text-sm font-bold text-amber-950"
            >
              إلغاء
            </button>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-[1fr_auto]">
            <input
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) => setReplaceFile(e.target.files?.[0] || null)}
              className="rounded-lg border bg-white p-3"
            />
            <button
              type="submit"
              disabled={loading}
              className="rounded-lg bg-amber-500 px-5 py-3 font-bold text-amber-950 hover:bg-amber-400 disabled:opacity-60"
            >
              استبدال الآن
            </button>
          </div>
        </form>
      )}

      <section className="space-y-4">
        <div className="flex flex-col justify-between gap-3 md:flex-row md:items-center">
          <h3 className="text-xl font-extrabold text-gray-900">الوثائق المرفوعة</h3>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="بحث بالرقم أو العنوان أو اسم الملف"
            className="w-full rounded-lg border p-3 md:max-w-sm"
          />
        </div>

        {loadingList ? (
          <div className="rounded-lg border bg-white p-5 text-center text-gray-500">جاري تحميل الوثائق...</div>
        ) : filteredDocuments.length === 0 ? (
          <div className="rounded-lg border bg-white p-5 text-center text-gray-500">لا توجد وثائق مطابقة.</div>
        ) : (
          <div className="grid gap-4">
            {filteredDocuments.map((doc) => (
              <article key={doc._id} className="rounded-xl border bg-white p-4 shadow-sm">
                <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-center">
                  <div className="min-w-0">
                    <p className="font-mono text-lg font-black text-blue-800" dir="ltr">{doc.serialNumber}</p>
                    <h4 className="mt-1 truncate text-base font-bold text-gray-900">{doc.title || doc.originalName}</h4>
                    <p className="mt-1 truncate text-sm text-gray-500">{doc.originalName} - {formatSize(doc.size)}</p>
                    <p className="mt-1 break-all text-xs text-gray-500" dir="ltr">{makeVerifyLink(doc.serialNumber)}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <a
                      href={makeVerifyLink(doc.serialNumber)}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-bold text-white hover:bg-blue-800"
                    >
                      فتح صفحة التحقق
                    </a>
                    <button
                      onClick={() => copyText(doc.serialNumber, 'ID')}
                      className="rounded-lg border px-4 py-2 text-sm font-bold text-gray-700 hover:bg-gray-50"
                    >
                      نسخ ID
                    </button>
                    <button
                      onClick={() => copyText(makeVerifyLink(doc.serialNumber), 'الرابط')}
                      className="rounded-lg border px-4 py-2 text-sm font-bold text-gray-700 hover:bg-gray-50"
                    >
                      نسخ الرابط
                    </button>
                    <button
                      onClick={() => setReplaceTarget(doc)}
                      className="rounded-lg bg-amber-500 px-4 py-2 text-sm font-bold text-amber-950 hover:bg-amber-400"
                    >
                      استبدال
                    </button>
                    <button
                      onClick={() => handleDelete(doc)}
                      className="rounded-lg bg-red-600 px-4 py-2 text-sm font-bold text-white hover:bg-red-700"
                    >
                      حذف
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
